import React, { useEffect } from "react";
import _ from "lodash";
import cx from "classnames";

import { animated, useSpring, useTransition } from "@react-spring/web";
import styles from "./add.module.scss";
import { SearchBar } from "src/components/input";
import { TabBar } from "src/components/tabbar";
import {
  AssetPlaceholder,
  CollectionPlaceholder,
  CollectionsBrowser,
  EntityBrowser,
  StreamPlaceholder,
  StreamsBrowser,
} from "src/components/entityBrowser";
import { useDispatch } from "react-redux";
import {
  invalidateAssetsSearchResults,
  invalidateCollectionsSearchResults,
  invalidateStreamsSearchResults,
  searchAssets,
  searchCollections,
  searchStreams,
} from "src/redux/actions/search";
import { useSelector } from "react-redux";
import { USER_DEFINED } from "src/constants/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import Tooltip from "src/components/tooltip";
import userRoleService from "src/utils/userRoles.service";

const isInternal = (entity) => {
  switch (entity.entityType) {
    case "collection":
      return entity.internal;
    case "asset":
      return !entity.settings.share;
    default:
      return false;
  }
};

interface AddAssetsFromHubProps {
  style: any;
  width: number;
  selectableEntityTypes: string[];
  allowInternalEntities?: boolean;

  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  source?: any;
  className?: string;
}

const AddAssetsFromHub: React.FC<AddAssetsFromHubProps> = ({
  style,
  className,
  source,
  width,
  allowInternalEntities,
  selectableEntityTypes,
  onSelection,
  entityAction,
}) => {
  const [query, setQuery] = React.useState<string | null>("");

  const browserTransition = useTransition(!query, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const searchTransition = useTransition(query, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleQueryChange = (value) => {
    setQuery(value);
  };

  return (
    <animated.div className={styles.hubAssets} style={style}>
      <div className={cx(styles.searchBar, className)}>
        <SearchBar query={query} onSearch={handleQueryChange} />
      </div>
      <div className={cx(styles.stacks, styles.content)}>
        {browserTransition(
          (style, item) =>
            item && (
              <AssetBrowser
                style={style}
                width={width}
                allowInternalEntities={allowInternalEntities}
                selectableEntityTypes={selectableEntityTypes}
                onSelection={onSelection}
                source={source}
                className={className}
                entityAction={entityAction}
              />
            ),
        )}
        {searchTransition(
          (style, item) =>
            item && (
              <SearchResults
                style={style}
                width={width}
                allowInternalEntities={allowInternalEntities}
                selectableEntityTypes={selectableEntityTypes}
                query={query}
                onSelection={onSelection}
                source={source}
                entityAction={entityAction}
                className={className}
              />
            ),
        )}
      </div>
    </animated.div>
  );
};

interface AssetBrowserProps {
  style: any;
  width: number;
  selectableEntityTypes: string[];
  allowInternalEntities?: boolean;

  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  source?: any;
  className?: string;
}

const AssetBrowser: React.FC<AssetBrowserProps> = ({
  style,
  width,
  source,
  className,
  allowInternalEntities,
  selectableEntityTypes,
  onSelection,
  entityAction
}) => {
  const [activeTab, setActiveTab] = React.useState(0);

  const handleSelection = (entity) => {
    if (source && (source.type === "stream" || source.type === "group")) {
      onSelection(entity);
    } else if (entity.entityType === "collection") {
      if (allowInternalEntities || !entity.internal) {
        onSelection(entity);
      }
    } else if (entity.entityType === "asset") {
      if (allowInternalEntities || entity.settings?.share) {
        onSelection(entity);
      }
    }
  };

  return (
    <animated.div
      style={style}
      className={cx(styles.assetBrowser, styles.stack)}
    >
      <TabBar
        className={cx(styles.tabs, className)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={(tab) => setActiveTab(tab)}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Streams
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Collections
        </div>
      </TabBar>
      <div
        className={cx(styles.stacks, styles.tabContainer)}
        style={{ width: width }}
      >
        <TabSection index={0} selected={activeTab}>
          <StreamsBrowser
            width={width - 60}
            action={(entity) => {
              
              if (entityAction) {
                return entityAction(entity);
              }

              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);
                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          internal ? "paperflite-lock" : "paperflite-add",
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }
              return null
            }}
            onSelection={handleSelection}
          />
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <CollectionsBrowser
            width={width - 60}
            action={(entity) => {
              if (entityAction) {
                return entityAction(entity);
              }

              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);
                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          internal ? "paperflite-lock" : "paperflite-add",
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }


              return null
            }}
            onSelection={handleSelection}
          />
        </TabSection>
      </div>
    </animated.div>
  );
};

interface SearchResultsProps {
  style: any;
  width: number;
  source?: any;
  query?: string | null;
  allowInternalEntities?: boolean;
  selectableEntityTypes: string[];
  className?: string;

  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  style,
  width,
  query,
  allowInternalEntities,
  selectableEntityTypes,
  onSelection,
  entityAction,
  source,
  className,
}) => {
  const { streams, collections, assets } = useSelector((state: any) => {
    const streamState = _.get(state, "search.streams");
    const collectionState = _.get(state, "search.collections");
    const assetState = _.get(state, "search.assets");

    const streams = _.get(streamState, "pagination.totalRecordsCount", 0);
    const collections = _.get(
      collectionState,
      "pagination.totalRecordsCount",
      0,
    );
    const assets = _.get(assetState, "pagination.totalRecordsCount", 0);

    return {
      streams: streams,
      collections: collections,
      assets: assets,
    };
  }, _.isEqual);

  const [activeTab, setActiveTab] = React.useState(0);

  const handleSelection = (entity) => {
    if (source && (source.type === "stream" || source.type === "group")) {
      onSelection(entity);
    } else if (entity.entityType === "collection") {
      if (allowInternalEntities || !entity.internal) {
        onSelection(entity);
      }
    } else if (entity.entityType === "asset") {
      if (allowInternalEntities || entity.settings?.share) {
        onSelection(entity);
      }
    }
  };

  return (
    <animated.div
      style={style}
      className={cx(styles.searchResults, styles.stack)}
    >
      <TabBar
        className={cx(styles.tabs, className)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={(tab) => setActiveTab(tab)}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Assets{assets > 0 ? `(${assets})` : ""}
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Streams{streams > 0 ? `(${streams})` : ""}
        </div>
        {userRoleService.canAccessCollection() && (
          <div className={cx(styles.tab, { [styles.active]: activeTab == 2 })}>
            Collections{collections > 0 ? `(${collections})` : ""}
          </div>
        )}
      </TabBar>
      <div className={cx(styles.stacks, styles.tabContainer)}>
        <TabSection index={0} selected={activeTab}>
          <AssetSearchResult
            query={query}
            width={width - 60}
            action={(entity) => {
              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);

                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          !entity.settings.share
                            ? "paperflite-lock"
                            : "paperflite-add",
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }

              if (entityAction) {
                return entityAction(entity);
              }

              return false;
            }}
            onSelection={handleSelection}
          />
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <StreamSearchResult
            query={query}
            width={width - 60}
            action={(entity) => {
              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);

                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          internal ? "paperflite-lock" : "paperflite-add",
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }

              if (entityAction) {
                return entityAction(entity);
              }

              return false;
            }}
            onSelection={handleSelection}
          />
        </TabSection>
        {userRoleService.canAccessCollection() && (
          <TabSection index={2} selected={activeTab}>
            <CollectionSearchResult
              query={query}
              width={width - 60}
              action={(entity) => {
                if (
                  _.size(selectableEntityTypes) > 0 &&
                  _.includes(selectableEntityTypes, entity.entityType)
                ) {
                  if (
                    entity.entityType === "collection" &&
                    entity.sectionsCount == 0 &&
                    entity.assetsCount == 0
                  ) {
                    return false;
                  }

                  const internal = !allowInternalEntities && isInternal(entity);

                  return (
                    <Tooltip
                      content="Internal use only"
                      placement="auto"
                      disabled={!internal}
                    >
                      <button className={styles.action}>
                        <i
                          className={cx(
                            styles.icon,
                            internal ? "paperflite-lock" : "paperflite-add",
                          )}
                        ></i>
                      </button>
                    </Tooltip>
                  );
                }

                if (entityAction) {
                  return entityAction(entity);
                }

                return false;
              }}
              onSelection={handleSelection}
            />
          </TabSection>
        )}
      </div>
    </animated.div>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      transform: active
        ? `translate3d(0,0,0)`
        : `translate3d(${100 * (index > selected ? 1 : -1)}%, 0, 0)`,
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  );
};

interface CollectionSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const CollectionSearchResult: React.FC<CollectionSearchResultProps> = ({
  className,
  query,
  width,
  action,
  onSelection,
}) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const collectionState = _.get(state, "search.collections");
      const loading = _.get(collectionState, "loading");
      const pagination = _.get(collectionState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const collections = (_.get(collectionState, "items") || []).filter(
        (item: any) => {
          return item.type === USER_DEFINED;
        },
      );

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        collections: collections,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  useEffect(() => {
    searchCollectionForPage(0);
  }, []);
  const searchCollectionForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if (page <= 0) {
        dispatch(invalidateCollectionsSearchResults());
      }
      dispatch(searchCollections(request));
    }
  };

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      searchCollectionForPage(nextPage);
    }
  };

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (
              <>
                <CollectionPlaceholder width={"50%"} />
                <CollectionPlaceholder width={"70%"} />
                <CollectionPlaceholder width={"60%"} />
                <CollectionPlaceholder width={"50%"} />
              </>
            );
          } else {
            return <CollectionPlaceholder width={"70%"} />;
          }
        }}
      >
        {_.size(collections) === 0 && !loading && (
          <span className={styles.noRecordsFound}>No results found</span>
        )}
        {_.size(collections) > 0 && (
          <EntityBrowser
            className={className}
            width={width}
            items={collections}
            action={action}
            onSelection={onSelection}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

interface StreamSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const StreamSearchResult: React.FC<StreamSearchResultProps> = ({
  className,
  query,
  width,
  action,
  onSelection,
}) => {
  const dispatch = useDispatch();

  const { loading, streams, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const streamState = _.get(state, "search.streams");
      const loading = _.get(streamState, "loading");
      const pagination = _.get(streamState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const streams = _.get(streamState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        streams: streams,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  useEffect(() => {
    searchStreamForPage(0);
  }, []);
  const searchStreamForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if (page <= 0) {
        dispatch(invalidateStreamsSearchResults());
      }
      dispatch(searchStreams(request));
    }
  };

  const handleLoadMoreStreams = () => {
    if (nextPage) {
      searchStreamForPage(nextPage);
    }
  };

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreStreams}
        loader={() => {
          if (_.size(streams) === 0) {
            return (
              <>
                <StreamPlaceholder width={"50%"} />
                <StreamPlaceholder width={"70%"} />
                <StreamPlaceholder width={"60%"} />
                <StreamPlaceholder width={"50%"} />
              </>
            );
          } else {
            return <StreamPlaceholder width={"70%"} />;
          }
        }}
      >
        {_.size(streams) === 0 && !loading && (
          <span className={styles.noRecordsFound}>No results found</span>
        )}
        {_.size(streams) > 0 && (
          <EntityBrowser
            className={className}
            width={width}
            items={streams}
            action={action}
            onSelection={onSelection}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

interface AssetSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const AssetSearchResult: React.FC<AssetSearchResultProps> = ({
  className,
  query,
  width,
  action,
  onSelection,
}) => {
  const dispatch = useDispatch();

  const { loading, assets, nextPage, hasMoreItems } = useSelector(
    (state: any) => {
      const assetState = _.get(state, "search.assets");
      const loading = _.get(assetState, "loading");
      const pagination = _.get(assetState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const assets = _.get(assetState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        assets: assets,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  useEffect(() => {
    searchAssetForPage(0);
  }, []);

  const searchAssetForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if (page <= 0) {
        dispatch(invalidateAssetsSearchResults());
      }
      dispatch(searchAssets(request));
    }
  };

  const handleLoadMoreAssets = () => {
    if (nextPage) {
      searchAssetForPage(nextPage);
    }
  };

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreAssets}
        loader={() => {
          if (_.size(assets) === 0) {
            return (
              <>
                <AssetPlaceholder />
                <AssetPlaceholder />
                <AssetPlaceholder />
                <AssetPlaceholder />
              </>
            );
          } else {
            return <AssetPlaceholder />;
          }
        }}
      >
        {_.size(assets) === 0 && !loading && (
          <span className={styles.noRecordsFound}>No results found</span>
        )}
        {_.size(assets) > 0 && (
          <EntityBrowser
            className={className}
            width={width}
            items={assets}
            action={action}
            onSelection={onSelection}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

export { AddAssetsFromHub };
